import React, { useState } from "react";

import { Container, Row, Col } from "react-bootstrap";
import { MDBDataTableV5 } from "mdbreact";
import { TextField } from "@material-ui/core";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Skeleton from "@material-ui/lab/Skeleton";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import swal from "sweetalert";
import exportFromJSON from "export-from-json";

import CustomGreenButton from "../../../../components/shared/custom-green-button";
import TableColumnDropdown from "../../../../components/shared/table-column-dropdown/table-column-dropdown";
import { GENERAL_URL_API } from "../../../../shared/urls";
import { columnasRecomendacionPaquetes } from "../../../../shared/data/columnas";
import { FUENTE_PLAN_LECTOR } from "./filtros/filter-registry";

import AlcanceSelector from "./alcance-selector";
import GrupoCondiciones from "./filtros/grupo-condiciones";
import { useOpcionesFiltros } from "./filtros/filter-registry";
import ListaBloques, {
  BLOQUES_POR_DEFECTO,
  totalPorcentaje,
} from "./bloques/lista-bloques";
import PlantillaBarra from "./plantillas/plantilla-barra";

const API_ENDPOINT = `${GENERAL_URL_API}/recomendacion/generarRecomendacion.php`;

const CONFIG_INICIAL = {
  version: 2,
  nombre: "",
  cantidad: 100,
  alcance: { superInstituto: "", institutos: [], clientes: [] },
  condicionesGlobales: [],
  bloques: BLOQUES_POR_DEFECTO,
};

/** Deja la configuración con los tipos que espera el backend. */
const normalizarConfig = (config) => ({
  ...config,
  version: 2,
  nombre: (config.nombre || "").trim(),
  cantidad: Number(config.cantidad) || 0,
  bloques: (config.bloques || []).map((bloque) => ({
    ...bloque,
    porcentaje: Number(bloque.porcentaje) || 0,
  })),
});

/** Completa una configuración cargada desde plantilla con los campos faltantes. */
const hidratarConfig = (config) => ({
  ...CONFIG_INICIAL,
  ...config,
  alcance: { ...CONFIG_INICIAL.alcance, ...(config?.alcance ?? {}) },
  condicionesGlobales: config?.condicionesGlobales ?? [],
  bloques: config?.bloques?.length ? config.bloques : BLOQUES_POR_DEFECTO,
});

const RecomendacionPaquetes = () => {
  const [config, setConfig] = useState(CONFIG_INICIAL);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [paises, setPaises] = useState([]);
  const [nombreGenerado, setNombreGenerado] = useState("");

  const { opciones: opcionesApi, cargando: cargandoOpciones } =
    useOpcionesFiltros();

  const setCampo = (campo, valor) =>
    setConfig((previo) => ({ ...previo, [campo]: valor }));

  const changeColumnStatus = (columnField) => {
    const temp = [...columns];
    const index = temp.findIndex((column) => column.field === columnField);
    temp[index].isVisible = !temp[index].isVisible;
    setColumns(temp);
  };

  const buildFormData = (descargar = false) => {
    const fd = new FormData();
    fd.append("config", JSON.stringify(normalizarConfig(config)));
    if (descargar) fd.append("descargar", 1);
    return fd;
  };

  const validar = async () => {
    if (!config.alcance.superInstituto) {
      await swal({
        title: "Error",
        text: "Selecciona un Super Instituto",
        icon: "error",
      });
      return false;
    }
    if (!config.nombre.trim()) {
      await swal({
        title: "Error",
        text: "Ingresa un nombre para la preselección",
        icon: "error",
      });
      return false;
    }
    if (!config.cantidad || Number(config.cantidad) <= 0) {
      await swal({
        title: "Error",
        text: "La cantidad de libros debe ser mayor a cero",
        icon: "error",
      });
      return false;
    }
    if (Math.round(totalPorcentaje(config.bloques)) !== 100) {
      await swal({
        title: "Error",
        text: "Los porcentajes de los bloques deben sumar exactamente 100",
        icon: "error",
      });
      return false;
    }
    const bloqueSinInstituto = config.bloques.find(
      (bloque) =>
        bloque.fuente === FUENTE_PLAN_LECTOR &&
        !(bloque.institutoPlanLector || "").trim()
    );
    if (bloqueSinInstituto) {
      await swal({
        title: "Error",
        text: `Selecciona un instituto plan lector en el bloque "${
          bloqueSinInstituto.nombre || "sin nombre"
        }"`,
        icon: "error",
      });
      return false;
    }
    return true;
  };

  const limpiarResultado = () => {
    setRows([]);
    setColumns([]);
    setResumen(null);
    setCuotas([]);
    setPaises([]);
  };

  /**
   * Bloques que no llenaron su cuota: sus condiciones fueron demasiado estrechas o
   * sus libros ya habían salido en un bloque anterior.
   */
  const bloquesIncompletos = () => {
    if (!resumen) return [];
    return Object.entries(resumen)
      .map(([bloque, obtenidos], indice) => ({
        bloque,
        obtenidos,
        cuota: cuotas[indice] ?? 0,
      }))
      .filter((item) => item.obtenidos < item.cuota);
  };

  const handleGenerar = async () => {
    if (!(await validar())) return;

    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINT, {
        method: "POST",
        credentials: "include",
        body: buildFormData(),
      });
      const resJSON = await res.json();

      if (resJSON.status !== 1) {
        await swal({
          title: "Error",
          text: resJSON.info || "No se pudo generar la preselección",
          icon: "error",
        });
        limpiarResultado();
        setLoading(false);
        return;
      }

      setRows(resJSON.data || []);
      setColumns(
        columnasRecomendacionPaquetes.map((col) => ({ ...col, isVisible: true }))
      );
      setResumen(resJSON.resumen || null);
      setCuotas(resJSON.cuotas || []);
      setPaises(resJSON.paises || []);
      setNombreGenerado(config.nombre.trim());
    } catch (e) {
      await swal({
        title: "Error",
        text: e.message || "No se pudo generar la preselección",
        icon: "error",
      });
      limpiarResultado();
    }
    setLoading(false);
  };

  const downloadExcelFromApi = async () => {
    const res = await fetch(API_ENDPOINT, {
      method: "POST",
      credentials: "include",
      body: buildFormData(true),
    });

    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      const errJson = contentType.includes("application/json")
        ? await res.json()
        : null;
      throw new Error(errJson?.info || `Error ${res.status}`);
    }
    if (contentType.includes("application/json")) {
      const err = await res.json();
      throw new Error(err?.info || "No se pudo generar el archivo");
    }

    const blob = await res.blob();
    let filename = `preseleccion-${new Date().toISOString().split("T")[0]}.xlsx`;
    const disposition = res.headers.get("content-disposition") || "";
    const match = disposition.match(
      /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
    );
    const extracted = match ? match[1] || match[2] : null;
    if (extracted) {
      try {
        filename = decodeURIComponent(extracted);
      } catch (_) {
        filename = extracted;
      }
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const nombreArchivo = () =>
    "recomendacion-paquetes-" +
    (nombreGenerado || "preseleccion").replace(/\s+/g, "-") +
    "-" +
    new Date().toISOString().split("T")[0];

  const handleCsvExport = () => {
    exportFromJSON({
      data: rows,
      fileName: nombreArchivo(),
      exportType: exportFromJSON.types.csv,
    });
  };

  const handleJsonExport = () => {
    exportFromJSON({
      data: rows,
      fileName: nombreArchivo(),
      exportType: exportFromJSON.types.json,
    });
  };

  const handleExcelExport = async () => {
    try {
      await downloadExcelFromApi();
    } catch (e) {
      await swal({
        title: "Error",
        text: e.message || "No se pudo descargar el archivo",
        icon: "error",
      });
    }
  };

  return (
    <>
      <div className="mb-4 d-flex align-items-center">
        <div className="admin-page-tilte">(</div>
        <LibraryBooksIcon />
        <div className="admin-page-tilte mr-2">)</div>
        <div className="admin-page-tilte">
          Recomendación de paquetes de renovación
        </div>
      </div>
      <Container fluid>
        <Row>
          <Col xs={12} className="content-white-box px-4 py-4">
            <PlantillaBarra
              config={normalizarConfig(config)}
              onCargar={(cargada) => {
                setConfig(hidratarConfig(cargada));
                limpiarResultado();
              }}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12} className="content-white-box px-4 py-4">
            <AlcanceSelector
              alcance={config.alcance}
              onChange={(alcance) => setCampo("alcance", alcance)}
            />

            <label className="admin-label-text mt-4 d-block">
              Nombre de la preselección:
            </label>
            <TextField
              value={config.nombre}
              onChange={(e) => setCampo("nombre", e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Ej: Renovación 2026 - Colegio ABC"
              style={{ width: "100%", fontFamily: "'Noto Sans JP', sans-serif" }}
            />

            <label className="admin-label-text mt-3 d-block">
              Cantidad de libros recomendados:
            </label>
            <TextField
              value={config.cantidad}
              onChange={(e) => setCampo("cantidad", e.target.value)}
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              style={{ width: "180px", fontFamily: "'Noto Sans JP', sans-serif" }}
            />

            <div className="mt-4">
              <GrupoCondiciones
                condiciones={config.condicionesGlobales}
                onChange={(condiciones) =>
                  setCampo("condicionesGlobales", condiciones)
                }
                opcionesApi={opcionesApi}
                titulo="Condiciones globales"
                descripcion="Aplican a todos los bloques. Los libros gratuitos e inactivos siempre quedan fuera."
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12} className="content-white-box px-4 py-4">
            <ListaBloques
              bloques={config.bloques}
              onChange={(bloques) => setCampo("bloques", bloques)}
              opcionesApi={opcionesApi}
              cantidad={Number(config.cantidad) || 0}
            />

            <CustomGreenButton
              variant="contained"
              className="mt-4"
              onClick={handleGenerar}
              disabled={loading || cargandoOpciones}
            >
              {loading ? "Generando..." : "Generar preselección"}
            </CustomGreenButton>
          </Col>
        </Row>

        <Row>
          <Col xs={12} className="content-white-box px-4 py-4">
            <div className="d-flex align-items-center">
              <MenuBookIcon />
              <div className="admin-main-title-dark-gray ml-2 mt-1">
                Preselección de libros
                {nombreGenerado ? ` — ${nombreGenerado}` : ""}
              </div>
            </div>
            <div className="admin-muted-text-small mt-1 mb-4">
              Libros recomendados por bloque para renovación de paquetes.
            </div>

            {resumen && (
              <>
                <div className="admin-muted-text-small mb-1">
                  Resumen:{" "}
                  {Object.entries(resumen)
                    .map(([bloque, cantidad]) => `${bloque} (${cantidad})`)
                    .join(" · ")}
                  {paises.length > 0 && ` · Territorio: ${paises.join(", ")}`}
                </div>
                {bloquesIncompletos().length > 0 && (
                  <div className="admin-muted-text-small mb-3 text-danger">
                    Bloques por debajo de su cuota:{" "}
                    {bloquesIncompletos()
                      .map(
                        (item) =>
                          `${item.bloque} (${item.obtenidos} de ${item.cuota})`
                      )
                      .join(" · ")}
                    . El faltante se repartió entre los demás bloques.
                  </div>
                )}
              </>
            )}

            {rows.length <= 0 || columns.length <= 0 ? (
              <>
                <Skeleton animation="wave" />
                <Skeleton animation="wave" />
                <Skeleton animation="wave" />
                <Skeleton animation="wave" />
              </>
            ) : (
              <>
                <TableColumnDropdown
                  columns={columns}
                  changeColumnStatus={changeColumnStatus}
                />
                <br />
                <ButtonGroup size="small" variant="contained" className="my-3">
                  <CustomGreenButton
                    onClick={() => {
                      navigator.clipboard
                        .writeText(JSON.stringify([...rows]))
                        .then(async () => {
                          await swal({
                            title: "Se ha copiado la tabla!",
                            icon: "success",
                          });
                        });
                    }}
                  >
                    Copy
                  </CustomGreenButton>
                  <CustomGreenButton onClick={handleCsvExport}>
                    CSV
                  </CustomGreenButton>
                  <CustomGreenButton onClick={handleJsonExport}>
                    JSON
                  </CustomGreenButton>
                  <CustomGreenButton onClick={handleExcelExport}>
                    EXCEL
                  </CustomGreenButton>
                </ButtonGroup>
                <MDBDataTableV5
                  small
                  responsive
                  searchTop
                  searchBottom={false}
                  barReverse
                  pagingTop
                  striped
                  bordered
                  data={{
                    columns: columns.filter((obj) => obj.isVisible),
                    rows: rows,
                  }}
                  noBottomColumns
                  order={["posicion", "asc"]}
                  entriesOptions={[5, 10, 20, 50]}
                  entriesLabel="Numero de filas"
                  searchLabel="Buscar"
                  paginationLabel={["Anterior", "Siguiente"]}
                  infoLabel={["Mostrando filas", "a", "de", "filas"]}
                  fullPagination
                />
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default RecomendacionPaquetes;
